import React from "react";
import { CheckCircle, XCircle, AlertTriangle, Wrench, Timer, Clock, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import type { TechnicalChecks, TechCheckStatus } from "@/hooks/useTechnicalChecks";
import { cite, academicSources } from "@/data/research-sources";

interface ActionPlanProps {
  websiteUrl?: string;
  techStatus: TechCheckStatus;
  techData: TechnicalChecks | null;
}

/** Extract bare domain from a URL, e.g. "https://www.seatpick.com/foo" → "seatpick.com" */
function extractDomain(url?: string): string {
  if (!url) return 'yoursite.com';
  try {
    const host = new URL(url.startsWith('http') ? url : `https://${url}`).hostname;
    return host.replace(/^www\./, '');
  } catch {
    return 'yoursite.com';
  }
}

/** Render a step string with dynamic domain links and known static URLs as clickable <a> tags. */
function renderStep(step: string, domain: string): React.ReactNode {
  const staticLinks: Record<string, string> = {
    'search.google.com/test/rich-results': 'https://search.google.com/test/rich-results',
    'schema.org/Organization': 'https://schema.org/Organization',
  };

  const ysDomainPattern = /yoursite\.com(\/[^\s,)]*)?/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = ysDomainPattern.exec(step)) !== null) {
    const before = step.slice(lastIndex, match.index);
    if (before) parts.push(before);
    const path = match[1] || '';
    const displayText = `${domain}${path}`;
    const href = `https://${domain}${path}`;
    parts.push(
      <a key={match.index} href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline">{displayText}</a>
    );
    lastIndex = match.index + match[0].length;
  }

  if (parts.length === 0) {
    for (const [text, href] of Object.entries(staticLinks)) {
      if (step.includes(text)) {
        const idx = step.indexOf(text);
        return (
          <>
            {step.slice(0, idx)}
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline">{text}</a>
            {step.slice(idx + text.length)}
          </>
        );
      }
    }
    return step;
  }

  const remaining = step.slice(lastIndex);
  if (remaining) parts.push(remaining);
  return <>{parts}</>;
}

// ── Check definitions ──
interface TechCheck {
  key: 'robotsTxt' | 'llmsTxt' | 'sitemap' | 'schemaMkp';
  title: string;
  passText: string;
  passDetail: string;
  failText: string;
  isWarning?: boolean;
  warningText?: string;
  unknownText?: string;
  unknownInsightText?: string;
  unknownSteps?: string[];
  timeEstimate: string;
  timeIcon: 'quick' | 'medium';
  linkText: string;
  linkUrl: string;
  tooltipText: string;
  insightText: string;
  steps: string[];
  footnote?: string;
}

const techChecks: TechCheck[] = [
  {
    key: 'robotsTxt',
    title: 'AI Crawler Access (robots.txt)',
    passText: 'AI crawlers are allowed on your site',
    passDetail: 'Your robots.txt allows AI crawlers (GPTBot, ClaudeBot, PerplexityBot) to access your site.',
    failText: 'AI crawlers are blocked. Fix your robots.txt',
    unknownText: 'Unable to verify. Site blocked our check. Verify robots.txt manually',
    unknownInsightText: 'We couldn\'t reach your robots.txt file due to server restrictions (e.g. rate limiting). This does not mean AI crawlers are blocked. Open your robots.txt manually to verify.',
    unknownSteps: [
      'Open yoursite.com/robots.txt in your browser',
      'Look for User-agent rules mentioning GPTBot, ClaudeBot, or PerplexityBot',
      'If no Disallow rules exist for these bots, your site is accessible to AI crawlers',
      'If you see Disallow: / for AI bots, consider removing those rules',
    ],
    timeEstimate: '15 min',
    timeIcon: 'quick',
    linkText: 'Guide →',
    linkUrl: 'https://platform.openai.com/docs/bots',
    tooltipText: `Many sites unknowingly block AI crawlers, preventing their content from appearing in AI responses. Research shows top 20 domains capture 67% of all AI citations (${cite('yang-2025')}). Being crawlable is the first step to being cited.`,
    insightText: 'Without proper robots.txt rules, AI crawlers have no access to your site\'s content.',
    steps: [
      'Open your robots.txt file (usually at yoursite.com/robots.txt)',
      'Check for User-agent: GPTBot, ClaudeBot, or PerplexityBot with Disallow: /',
      'Remove any Disallow rules for AI crawlers you want to allow',
      'If using User-agent: * with Disallow: /, add specific Allow rules for AI bots',
    ],
  },
  {
    key: 'sitemap',
    title: 'XML Sitemap',
    passText: 'Sitemap found and accessible',
    passDetail: 'Your sitemap.xml was found and is accessible to search engines and AI crawlers.',
    failText: 'No sitemap found. AI crawlers may miss your pages',
    unknownText: 'Unable to verify. Site blocked our check. Check /sitemap.xml manually',
    unknownInsightText: 'We couldn\'t verify your sitemap due to server restrictions. Check yoursite.com/sitemap.xml manually.',
    unknownSteps: [
      'Open yoursite.com/sitemap.xml in your browser',
      'Check if a valid XML file loads containing your page URLs',
      'If it exists, check Google Search Console to ensure it is submitted and indexed',
      'If you get a 404 error, you need to generate and upload a sitemap',
    ],
    timeEstimate: '30 min',
    timeIcon: 'quick',
    linkText: 'Generator →',
    linkUrl: 'https://www.xml-sitemaps.com/',
    tooltipText: 'Content that isn\'t indexed can\'t be cited in AI-powered search results. AI crawlers rely on sitemaps to discover pages efficiently. An industry standard recommended by Google Search Central.',
    insightText: 'AI crawlers rely on sitemaps to discover your pages. Missing pages can\'t be cited.',
    steps: [
      'Generate a sitemap.xml using an online generator or your CMS',
      'Upload it to your site root (yoursite.com/sitemap.xml)',
      'Submit it to Google Search Console',
      'Ensure all important pages are included',
    ],
  },
  {
    key: 'llmsTxt',
    title: 'llms.txt file',
    passText: 'llms.txt file found',
    passDetail: 'Your llms.txt file provides structured information about your brand for AI models.',
    failText: 'No llms.txt found, optional but recommended',
    isWarning: true,
    warningText: 'No llms.txt found, optional but recommended',
    timeEstimate: '30 min',
    timeIcon: 'quick',
    linkText: 'Template →',
    linkUrl: 'https://llmstxt.org/',
    tooltipText: 'Adoption is still very early, and no major AI platform has confirmed using it for citations. Including it is low-effort and future-facing. Think of it as optional preparation, not a ranking factor.',
    insightText: 'Adding llms.txt gives AI models structured access to your brand information. Most competitors don\'t have one yet.',
    steps: [
      'Create llms.txt file in your website root directory',
      'Include company overview, products/services, and key differentiators',
      'Add structured data about your brand\'s unique value propositions',
    ],
  },
  {
    key: 'schemaMkp',
    title: 'Structured Data (Schema Markup)',
    passText: 'Schema markup detected on your site',
    passDetail: 'Schema markup was detected, helping AI models understand your site\'s content.',
    failText: 'No schema markup found. AI can\'t read your brand info',
    unknownText: 'Unable to verify. Site blocked our check. Check page source for JSON-LD manually',
    unknownInsightText: 'We couldn\'t scan your page for structured data due to server restrictions. Use Google\'s Rich Results Test to verify.',
    unknownSteps: [
      'Go to search.google.com/test/rich-results',
      'Enter your webpage URL and run the test',
      'Check the results for valid Organization, FAQ, or other structured data items',
      'If none are detected, you will need to implement JSON-LD schema markup',
    ],
    timeEstimate: '1.5 hours',
    timeIcon: 'medium',
    linkText: 'Practical Guide →',
    linkUrl: 'https://schema.org/Organization',
    tooltipText: `Peer-reviewed research (KDD 2024) found that content optimization tactics can boost AI visibility by up to 40%. Structured data is the technical foundation that makes your brand machine-readable. Note: checks server-rendered JSON-LD only. Schema injected via JavaScript (e.g., GTM) may not be detected.`,
    insightText: 'Structured data helps AI models understand your brand, products, and content at a glance.',
    steps: [
      'Organization Schema: Logo, social profiles, contact info',
      'FAQ Schema: 5-10 common questions about your service',
      'Validate with Google\'s Rich Results Test',
    ],
    footnote: 'Official reference: schema.org/Organization',
  },
];



// ── Helpers ──
type CheckState = 'pass' | 'fail' | 'warning';

function getCheckState(check: TechCheck, data: TechnicalChecks): CheckState {
  if (check.key === 'robotsTxt') {
    if (data.robotsTxt.status === 'allowed') return 'pass';
    if (data.robotsTxt.status === 'partially_blocked' || data.robotsTxt.status === 'unknown') return 'warning';
    return 'fail';
  }
  if (check.key === 'llmsTxt') {
    return data.llmsTxt.status === 'found' ? 'pass' : 'warning';
  }
  const status = data[check.key].status;
  if (status === 'found') return 'pass';
  if (status === 'unknown') return 'warning';
  return 'fail';
}

function getStateIcon(state: CheckState) {
  switch (state) {
    case 'pass': return <CheckCircle className="w-5 h-5 text-success flex-shrink-0" />;
    case 'warning': return <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0" />;
    case 'fail': return <XCircle className="w-5 h-5 text-destructive flex-shrink-0" />;
  }
}

function getStateText(check: TechCheck, state: CheckState, data: TechnicalChecks): string {
  if (state === 'pass') return check.passText;
  if (state === 'warning') {
    // "unknown" status = site blocked our check (e.g. Cloudflare 429)
    const rawStatus = data[check.key].status;
    if (rawStatus === 'unknown' && check.unknownText) return check.unknownText;
    if (check.key === 'robotsTxt' && data.robotsTxt.status === 'partially_blocked') {
      return `Partially blocked: ${data.robotsTxt.blockedBots.join(', ')}`;
    }
    return check.warningText || check.failText;
  }
  return check.failText;
}

const getTimeIcon = (type: 'quick' | 'medium') => {
  switch (type) {
    case 'quick': return <Timer className="w-3 h-3" />;
    case 'medium': return <Clock className="w-3 h-3" />;
  }
};

const getTimeBadgeColor = (type: 'quick' | 'medium') => {
  switch (type) {
    case 'quick': return 'bg-success/10 text-success border-success/20';
    case 'medium': return 'bg-warning/10 text-warning border-warning/20';
  }
};


// ── Component ──
const ActionPlan = ({ websiteUrl, techStatus, techData }: ActionPlanProps) => {
  const domain = extractDomain(websiteUrl);

  // Determine which accordion items should be auto-expanded (failed checks)
  const defaultOpenItems: string[] = [];
  if (techData) {
    techChecks.forEach((check) => {
      const state = getCheckState(check, techData);
      if (state === 'fail') {
        defaultOpenItems.push(check.key);
      }
    });
  }

  const showTechnicalSection = !!websiteUrl;

  return (
    <div id="technical-ai-checks" className="space-y-8">
      {/* Section 1: Technical AI-Readiness */}
      {showTechnicalSection && (
        <div className="space-y-3">
          <div className="space-y-1">
            <h3 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
              <Wrench className="w-5 h-5 text-primary" />
              Technical AI Checks
            </h3>
            <p className="text-sm text-muted-foreground">
              Checks if AI crawlers can find and read your site
            </p>
          </div>

          {techStatus === 'loading' && (
            <div className="space-y-2">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          )}

          {techStatus === 'error' && (
            <div className="p-4 rounded-lg border border-border bg-muted/30 text-sm text-muted-foreground">
              Unable to run technical checks. Try refreshing the page.
            </div>
          )}

          {techStatus === 'done' && techData && (
            <Accordion
              type="multiple"
              defaultValue={defaultOpenItems}
              className="space-y-1"
            >
              {techChecks.map((check) => {
                const state = getCheckState(check, techData);
                const stateText = getStateText(check, state, techData);
                const rawStatus = techData[check.key].status;
                const isUnknown = rawStatus === 'unknown';

                return (
                  <AccordionItem
                    key={check.key}
                    value={check.key}
                    className="border rounded-lg px-4 data-[state=open]:bg-muted/20"
                  >
                    <AccordionTrigger className="hover:no-underline py-3 gap-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0 text-left">
                        {getStateIcon(state)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium">{check.title}</span>
                             {state === 'fail' && (
                               <Badge variant="outline" className="text-destructive border-destructive/30 text-[10px] px-1.5 py-0">
                                Action needed
                              </Badge>
                            )}
                          </div>
                          <p className={cn(
                            "text-xs mt-0.5",
                            state === 'pass' ? 'text-success' : state === 'warning' ? 'text-warning' : 'text-destructive'
                          )}>
                            {stateText}
                          </p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                       {state === 'pass' ? (
                         <div className="pl-8 pb-1">
                           <p className="text-sm text-muted-foreground">{check.passDetail}</p>
                           <p className="text-sm text-muted-foreground mt-2 italic">{check.tooltipText}</p>
                         </div>
                       ) : (
                        <div className="pl-8 pb-1 space-y-3">
                           <p className="text-xs text-muted-foreground italic">
                             {isUnknown && check.unknownInsightText ? check.unknownInsightText : check.insightText}
                           </p>
                           <p className="text-sm text-muted-foreground mt-2 italic">{check.tooltipText}</p>
                           <div className="flex items-center gap-3 flex-wrap">
                            <span className={cn(
                              "flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border",
                              getTimeBadgeColor(check.timeIcon)
                            )}>
                              {getTimeIcon(check.timeIcon)}
                              {check.timeEstimate}
                            </span>
                            <a
                              href={check.linkUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary font-medium hover:underline flex items-center gap-1"
                            >
                              {check.linkText}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                          <ul className="space-y-1.5">
                            {(isUnknown && check.unknownSteps ? check.unknownSteps : check.steps).map((step, i) => (
                              <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                                <span className="text-primary mt-0.5">•</span>
                                <span>{renderStep(step, domain)}</span>
                              </li>
                            ))}
                          </ul>
                          {check.footnote && (
                            <p className="text-[10px] text-muted-foreground/70 italic">{check.footnote}</p>
                          )}
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </div>
      )}
    </div>
  );
};

export default ActionPlan;
